import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useAdminsQuery } from "../services/useAdminsQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { BackButton } from "@/shared/layout/BackButton";
import { EditForm } from "../components/EditForm";
import { PasswordForm } from "../components/PasswordForm";
import { LoadingSkeleton, ErrorState, SectionCard } from "@a2z/ui";

type AdminAdminEditPageClientProps = {
  locale: string;
  adminId: string;
};

export function EditAdminPage({ locale, adminId }: AdminAdminEditPageClientProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin =
    session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);
  const { data: admins, isLoading, error, refetch } = useAdminsQuery();

  const admin = useMemo(
    () => admins?.find((item) => item.id === adminId),
    [admins, adminId],
  );

  if (isSessionLoading || (!isSuperAdmin && !isSessionLoading) || isLoading) {
    return <LoadingSkeleton variant="card" />;
  }

  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  if (!admin) {
    return (
      <section className="flex w-full max-w-2xl flex-col gap-5 lg:px-6 lg:py-6">
        <BackButton
          label={t(keys.common.back)}
          fallbackHref={`/${locale}/admin/admins`}
        />
        <SectionCard title={t(keys.admin.adminsEditTitle)}>
          <div className="text-sm text-[color:var(--text-muted)]">
            {t(keys.errors.notFound)}
          </div>
        </SectionCard>
      </section>
    );
  }

  return (
    <section className="flex w-full max-w-2xl flex-col gap-5 lg:px-6 lg:py-6">
      <BackButton
        label={t(keys.common.back)}
        fallbackHref={`/${locale}/admin/admins`}
      />
      <SectionCard title={t(keys.admin.adminsEditTitle)}>
        <EditForm
          adminId={admin.id}
          defaultValues={{
            name: admin.name,
            email: admin.email,
            phone: admin.phone,
            roles: admin.roles,
          }}
        />
      </SectionCard>
      <SectionCard title={t(keys.common.password)}>
        <PasswordForm adminId={admin.id} />
      </SectionCard>
    </section>
  );
}
